// DEMO / SIMULATION ONLY.
// A self-contained animation of the on-chain rules (anti-sniper fee decay, fee water falling into the well,
// the well draining to staked holders pro-rata). It never reads or writes chain state, never touches real
// totals and never produces anything that looks like a real token, wallet or trade.

const TAU = Math.PI * 2;
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const lerp = (a, b, t) => a + (b - a) * t;

export const SIM = {
  feeStart: 15, // % at launch
  feeEnd: 4, // % after the decay
  platform: 1, // % of volume to the platform, the rest of the fee falls into the well
  decay: 60, // seconds of anti-sniper decay
  loop: 76, // seconds per simulated launch before it restarts
  drainTau: 7, // seconds (compressed: the real well drains with about an hour time constant)
};

export const SIM_HOLDERS = [
  { id: "A", share: 0.4 },
  { id: "B", share: 0.25 },
  { id: "C", share: 0.2 },
  { id: "D", share: 0.15 },
];

export function simFeePct(t) {
  if (t >= SIM.decay) return SIM.feeEnd;
  return SIM.feeStart - ((SIM.feeStart - SIM.feeEnd) * Math.max(0, t)) / SIM.decay;
}

const reducedMotion = () => typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches;

function sprite(r, g, b) {
  const c = document.createElement("canvas");
  c.width = c.height = 64;
  const x = c.getContext("2d");
  const gr = x.createRadialGradient(32, 32, 0, 32, 32, 32);
  gr.addColorStop(0, "rgba(255,255,255,1)");
  gr.addColorStop(0.22, `rgba(${r},${g},${b},0.85)`);
  gr.addColorStop(0.55, `rgba(${r},${g},${b},0.22)`);
  gr.addColorStop(1, `rgba(${r},${g},${b},0)`);
  x.fillStyle = gr;
  x.fillRect(0, 0, 64, 64);
  return c;
}

// fee heat 0 (4%) .. 1 (15%) -> colour between cyan and amber
function heatRgb(h) {
  const c0 = [0, 229, 255];
  const c1 = [255, 196, 87];
  return c0.map((v, i) => Math.round(lerp(v, c1[i], clamp(h, 0, 1))));
}

export class DemoSim {
  constructor(canvas, { onTick, onTrade, onRestart } = {}) {
    this.canvas = canvas;
    this.ctx = canvas.getContext("2d");
    this.onTick = onTick;
    this.onTrade = onTrade;
    this.onRestart = onRestart;
    this.reduced = reducedMotion();
    this.cyan = sprite(0, 229, 255);
    this.amber = sprite(255, 196, 87);
    this.holders = SIM_HOLDERS.map((h) => ({ ...h, recv: 0, shown: 0, acc: 0, flow: 0, pulse: 0 }));
    this.time = 0;
    this.visible = true;
    this.running = false;
    this.tickAcc = 0;
    this.#reset();

    this.ro = new ResizeObserver(() => this.#resize());
    this.ro.observe(canvas);
    this.#resize();
    this.io = new IntersectionObserver((es) => {
      this.visible = es.some((e) => e.isIntersecting);
      if (this.visible) this.#kick();
    });
    this.io.observe(canvas);
    this.onVis = () => !document.hidden && this.#kick();
    document.addEventListener("visibilitychange", this.onVis);
    this.mq = typeof matchMedia !== "undefined" ? matchMedia("(prefers-reduced-motion: reduce)") : null;
    this.onMq = () => {
      this.reduced = this.mq.matches;
      this.#kick();
    };
    this.mq?.addEventListener?.("change", this.onMq);
    this.#kick();
  }

  destroy() {
    this.destroyed = true;
    cancelAnimationFrame(this.raf);
    clearTimeout(this.slowTimer);
    this.ro.disconnect();
    this.io.disconnect();
    document.removeEventListener("visibilitychange", this.onVis);
    this.mq?.removeEventListener?.("change", this.onMq);
  }

  #reset() {
    this.t = 0;
    this.reservoir = 0;
    this.poured = 0;
    this.drunk = 0;
    this.trades = 0;
    this.nextTrade = 0.8;
    this.emptying = 0;
    this.level = 0.1;
    this.drops = [];
    this.splash = [];
    this.streams = [];
    this.popups = [];
    this.ripples = [];
    this.mist = [];
    for (const h of this.holders) {
      h.recv = 0;
      h.acc = 0;
      h.flow = 0;
    }
  }

  #kick() {
    if (this.destroyed || this.running) return;
    this.running = true;
    this.lastTs = performance.now();
    if (this.reduced) this.slowTimer = setTimeout(() => this.#slow(), 0);
    else this.raf = requestAnimationFrame((t) => this.#frame(t));
  }

  // reduced motion: no particles, the state advances twice a second
  #slow() {
    if (this.destroyed) return;
    if (!this.visible || document.hidden || !this.reduced) {
      this.running = false;
      if (!this.reduced) this.#kick();
      return;
    }
    this.#step(0.5);
    this.#draw();
    this.slowTimer = setTimeout(() => this.#slow(), 500);
  }

  #frame(ts) {
    if (this.destroyed) return;
    if (!this.visible || document.hidden || this.reduced) {
      this.running = false;
      if (this.reduced) this.#kick();
      return;
    }
    const dt = clamp((ts - this.lastTs) / 1000, 0, 0.05);
    this.lastTs = ts;
    this.#step(dt);
    this.#draw();
    this.raf = requestAnimationFrame((t) => this.#frame(t));
  }

  #resize() {
    const r = this.canvas.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.w = Math.max(1, r.width);
    this.h = Math.max(1, r.height);
    this.canvas.width = Math.round(this.w * dpr);
    this.canvas.height = Math.round(this.h * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.#layout();
    this.#draw(); // a frame is always present, even while paused offscreen
  }

  #layout() {
    const { w, h } = this;
    const narrow = w < 640;
    const R = narrow ? Math.min(w * 0.25, h * 0.2) : Math.min(w * 0.14, h * 0.26);
    const cy = narrow ? h * 0.28 : h * 0.4;
    this.g = { cx: w / 2, cy, R, r: R * 0.8, k: 0.38, lip: R * 0.12, bodyH: R * (narrow ? 0.7 : 0.8), narrow };
    const base = narrow ? Math.min(w * 0.085, 40) : clamp(w * 0.05, 40, 66);
    const bottom = narrow ? h * 0.83 : h * 0.8;
    const xs = narrow ? [0.385, 0.615, 0.135, 0.865].map((f) => w * f) : [-0.2, 0.2, -0.37, 0.37].map((f) => w / 2 + w * f);
    this.holders.forEach((hd, i) => {
      const bw = base * (0.62 + 0.95 * Math.sqrt(hd.share));
      hd.geo = { x: xs[i], bottom, bw, bh: bw * 1.12 };
    });
  }

  // ---------------------------------------------------------------- model
  #waterEllipse() {
    const g = this.g;
    const depth = 1 - this.level;
    const sc = 1 - 0.3 * depth;
    return { cx: g.cx, cy: g.cy + depth * g.r * g.k * 1.15 + g.r * g.k * 0.06, rx: g.r * sc, ry: g.r * g.k * sc, depth };
  }

  #meanInterval(t) {
    if (t < 8) return 0.42; // sniper rush right after launch
    if (t < SIM.decay) return 1.0;
    return 1.5;
  }

  #spawnTrade() {
    const t = this.t;
    const buy = Math.random() < (t < 8 ? 0.92 : 0.7);
    const size = Math.min(4, 0.12 + -Math.log(1 - Math.random()) * (buy ? 0.65 : 0.5));
    const fee = simFeePct(t);
    const toWell = (size * (fee - SIM.platform)) / 100;
    const heat = (fee - SIM.feeEnd) / (SIM.feeStart - SIM.feeEnd);
    const trade = { buy, size, fee, toWell, heat };
    if (this.reduced) return this.#land(trade);
    const W = this.#waterEllipse();
    const a = Math.random() * TAU;
    const rr = Math.sqrt(Math.random()) * 0.55;
    const u = Math.cos(a) * rr;
    const v = Math.sin(a) * rr * 0.7;
    this.drops.push({ ...trade, u, v, y: -20 - Math.random() * 30, vy: 120 + Math.random() * 60, s: clamp(2.6 + Math.sqrt(toWell) * 9, 3, 9), x: W.cx + u * W.rx });
  }

  #land(trade, u = 0, v = 0) {
    this.reservoir += trade.toWell;
    this.poured += trade.toWell;
    this.trades += 1;
    this.onTrade?.(trade);
    if (this.reduced) return;
    const W = this.#waterEllipse();
    const x = W.cx + u * W.rx;
    const y = W.cy + v * W.ry;
    this.ripples.push({ u, v, t: 0, s: 0.7 + trade.size * 0.25, heat: trade.heat });
    const n = Math.round(8 + trade.size * 6);
    for (let i = 0; i < n; i++) {
      const ang = -Math.PI / 2 + (Math.random() - 0.5) * 2.4;
      const sp = 60 + Math.random() * 140 * (0.6 + trade.size * 0.25);
      this.splash.push({ x, y, y0: y, vx: Math.cos(ang) * sp * 0.7, vy: Math.sin(ang) * sp, r: 2 + Math.random() * 4, t: 0, life: 0.6 + Math.random() * 0.5, heat: trade.heat });
    }
    if (this.splash.length > 240) this.splash.splice(0, this.splash.length - 240);
    this.popups.push({ x, y: y - 10, t: 0, text: `+${trade.toWell.toFixed(3)}`, heat: trade.heat });
    if (this.popups.length > 8) this.popups.shift();
    for (let i = 0; i < 3; i++) this.mist.push(this.#mistAt(x + (Math.random() - 0.5) * 20, y));
  }

  #mistAt(x, y) {
    return { x, y, vx: (Math.random() - 0.5) * 6, vy: -(8 + Math.random() * 10), r: 10 + Math.random() * 14, t: 0, life: 2.5 + Math.random() * 2 };
  }

  #step(dt) {
    this.time += dt;
    if (this.emptying > 0) {
      this.emptying -= dt;
      const k = Math.exp(-dt * 3.2);
      this.reservoir *= k;
      for (const h of this.holders) {
        h.recv *= k;
        h.shown *= k;
        h.flow *= k;
      }
      if (this.emptying <= 0) {
        this.#reset();
        this.onRestart?.();
      }
    } else {
      this.t += dt;
      this.nextTrade -= dt;
      if (this.nextTrade <= 0 && this.t < SIM.loop - 3) {
        this.#spawnTrade();
        this.nextTrade = -Math.log(1 - Math.random()) * this.#meanInterval(this.t) + 0.12;
      }
      if (this.t >= SIM.loop) {
        this.emptying = 1.6;
        this.drops = [];
      }
      // drain the well to the staked holders, pro-rata
      const d = this.reservoir * (1 - Math.exp(-dt / SIM.drainTau));
      this.reservoir -= d;
      this.drunk += d;
      for (const h of this.holders) {
        const part = d * h.share;
        h.recv += part;
        h.flow = lerp(h.flow, dt > 0 ? part / dt : 0, 1 - Math.exp(-dt * 4));
        if (!this.reduced) {
          h.acc += part * 300;
          while (h.acc >= 1) {
            h.acc -= 1;
            if (this.streams.length < 420) this.streams.push({ h, p: -Math.random() * 0.08, sp: 0.75 + Math.random() * 0.35, j: (Math.random() - 0.5) * 2 });
          }
        }
      }
    }
    for (const h of this.holders) {
      h.shown = lerp(h.shown, h.recv, 1 - Math.exp(-dt * 1.6));
      h.pulse *= Math.exp(-dt * 4);
    }
    this.level = lerp(this.level, 0.1 + 0.8 * (1 - Math.exp(-this.reservoir / 1.1)), 1 - Math.exp(-dt * 2.5));

    // particles
    const W = this.#waterEllipse();
    for (const d of this.drops) {
      d.vy += 820 * dt;
      d.y += d.vy * dt;
      const ty = W.cy + d.v * W.ry;
      if (d.y >= ty) {
        d.done = true;
        this.#land(d, d.u, d.v);
      }
    }
    this.drops = this.drops.filter((d) => !d.done);
    for (const p of this.splash) {
      p.t += dt;
      p.vy += 560 * dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
    }
    this.splash = this.splash.filter((p) => p.t < p.life && !(p.vy > 0 && p.y > p.y0 + 3));
    for (const r of this.ripples) r.t += dt;
    this.ripples = this.ripples.filter((r) => r.t < 2);
    for (const p of this.popups) p.t += dt;
    this.popups = this.popups.filter((p) => p.t < 1.4);
    for (const s of this.streams) {
      s.p += s.sp * dt;
      if (s.p >= 1) {
        s.done = true;
        s.h.pulse = Math.min(1, s.h.pulse + 0.25);
      }
    }
    this.streams = this.streams.filter((s) => !s.done);
    if (!this.reduced && Math.random() < dt * 2.2) this.mist.push(this.#mistAt(W.cx + (Math.random() - 0.5) * W.rx * 1.4, W.cy));
    for (const m of this.mist) {
      m.t += dt;
      m.x += m.vx * dt;
      m.y += m.vy * dt;
      m.r += dt * 6;
    }
    this.mist = this.mist.filter((m) => m.t < m.life);
    if (this.mist.length > 60) this.mist.splice(0, this.mist.length - 60);

    this.tickAcc += dt;
    if (this.tickAcc >= 0.12) {
      this.tickAcc = 0;
      this.onTick?.(this.state());
    }
  }

  state() {
    return {
      t: this.t,
      emptying: this.emptying > 0,
      fee: simFeePct(this.t),
      reservoir: this.reservoir,
      poured: this.poured,
      drunk: this.drunk,
      trades: this.trades,
      holders: this.holders.map((h) => ({ id: h.id, share: h.share, recv: h.recv })),
    };
  }

  // ---------------------------------------------------------------- drawing
  #path(h) {
    const g = this.g;
    const G = h.geo;
    const dir = Math.sign(G.x - g.cx) || 1;
    const sx = g.cx + dir * g.r * (g.narrow ? 0.45 : 0.55);
    const sy = g.cy - g.r * g.k * 0.2;
    const ex = G.x;
    const ey = G.bottom - G.bh + 4;
    const lift = g.narrow ? g.R * 0.55 : Math.abs(ex - sx) * 0.32 + g.R * 0.35;
    const cx = (sx + ex) / 2 + dir * (g.narrow ? g.R * 0.25 : 0);
    const cy = Math.min(sy, ey) - lift;
    return { sx, sy, cx, cy, ex, ey };
  }

  #draw() {
    const { ctx, w, h } = this;
    if (!this.g) return;
    ctx.clearRect(0, 0, w, h);
    this.#drawBackdrop();
    this.#drawFeeRing();
    this.#drawWell();
    this.#drawBuckets();
    this.#drawStreams();
    this.#drawDrops();
    this.#drawPopups();
    this.#drawWatermark();
  }

  #drawBackdrop() {
    const { ctx, w, h, g } = this;
    const halo = ctx.createRadialGradient(g.cx, g.cy, g.R * 0.2, g.cx, g.cy, Math.max(w, h) * 0.6);
    halo.addColorStop(0, "rgba(0,229,255,0.10)");
    halo.addColorStop(1, "rgba(0,229,255,0)");
    ctx.fillStyle = halo;
    ctx.fillRect(0, 0, w, h);
    // floor plane lines
    ctx.save();
    ctx.strokeStyle = "rgba(0,229,255,0.05)";
    ctx.lineWidth = 1;
    const floorY = g.cy + g.lip + g.bodyH;
    for (let i = 1; i <= 4; i++) {
      ctx.beginPath();
      ctx.ellipse(g.cx, floorY, g.R * (1.2 + i * 0.75), g.R * g.k * (1.2 + i * 0.75), 0, 0, TAU);
      ctx.stroke();
    }
    ctx.restore();
  }

  // anti-sniper timer: an amber arc on the floor ring that shrinks as the fee decays
  #drawFeeRing() {
    const { ctx, g } = this;
    const y = g.cy + g.lip + g.bodyH + 2;
    const rx = g.R * 1.38;
    const ry = g.R * g.k * 1.38;
    const left = clamp(1 - this.t / SIM.decay, 0, 1);
    ctx.save();
    ctx.lineCap = "round";
    ctx.strokeStyle = "rgba(0,229,255,0.16)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.ellipse(g.cx, y, rx, ry, 0, 0, TAU);
    ctx.stroke();
    if (left > 0 && this.emptying <= 0) {
      ctx.strokeStyle = "rgba(255,196,87,0.9)";
      ctx.shadowColor = "rgba(255,196,87,0.9)";
      ctx.shadowBlur = 14;
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.ellipse(g.cx, y, rx, ry, 0, -Math.PI / 2, -Math.PI / 2 + TAU * left);
      ctx.stroke();
      // head
      const a = -Math.PI / 2 + TAU * left;
      ctx.fillStyle = "#ffe2a6";
      ctx.beginPath();
      ctx.arc(g.cx + Math.cos(a) * rx, y + Math.sin(a) * ry, 3.2, 0, TAU);
      ctx.fill();
    }
    ctx.restore();
  }

  #drawWell() {
    const { ctx, g } = this;
    const R = g.R;
    const r = g.r;
    const k = g.k;
    const Rb = R * 0.94;
    const y0 = g.cy + g.lip * 0.6;
    const y1 = y0 + g.bodyH;
    // ground shadow
    const sh = ctx.createRadialGradient(g.cx, y1, 0, g.cx, y1, Rb * 1.6);
    sh.addColorStop(0, "rgba(0,0,0,0.6)");
    sh.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = sh;
    ctx.beginPath();
    ctx.ellipse(g.cx, y1, Rb * 1.6, Rb * k * 1.4, 0, 0, TAU);
    ctx.fill();
    // body
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(g.cx - Rb, y0);
    ctx.lineTo(g.cx - Rb, y1);
    ctx.ellipse(g.cx, y1, Rb, Rb * k, 0, Math.PI, 0, true);
    ctx.lineTo(g.cx + Rb, y0);
    ctx.closePath();
    const bg = ctx.createLinearGradient(g.cx - Rb, 0, g.cx + Rb, 0);
    bg.addColorStop(0, "#06080e");
    bg.addColorStop(0.3, "#1b2234");
    bg.addColorStop(0.5, "#242c41");
    bg.addColorStop(0.75, "#141a28");
    bg.addColorStop(1, "#05070c");
    ctx.fillStyle = bg;
    ctx.fill();
    ctx.clip();
    const rows = 4;
    const rowH = g.bodyH / rows;
    ctx.lineWidth = 1.2;
    for (let i = 1; i <= rows; i++) {
      ctx.strokeStyle = "rgba(2,4,8,0.85)";
      ctx.beginPath();
      ctx.ellipse(g.cx, y0 + i * rowH, Rb, Rb * k, 0, 0, Math.PI);
      ctx.stroke();
    }
    for (let i = 0; i < rows; i++) {
      for (let j = 0; j < 8; j++) {
        const a = ((j + (i % 2) * 0.5) / 8) * Math.PI;
        const x = g.cx + Math.cos(a) * Rb;
        const yt = y0 + i * rowH + Math.sin(a) * Rb * k;
        ctx.beginPath();
        ctx.moveTo(x, yt + 1);
        ctx.lineTo(x, yt + rowH - 1);
        ctx.stroke();
      }
    }
    const band = y0 + g.bodyH * 0.55;
    ctx.strokeStyle = `rgba(0,229,255,${0.25 + 0.12 * Math.sin(this.time * 1.2)})`;
    ctx.shadowColor = "#00e5ff";
    ctx.shadowBlur = 10;
    ctx.beginPath();
    ctx.ellipse(g.cx, band, Rb, Rb * k, 0, 0.15, Math.PI - 0.15);
    ctx.stroke();
    ctx.shadowBlur = 0;
    const ao = ctx.createLinearGradient(0, y0, 0, y0 + Rb * k * 2.4);
    ao.addColorStop(0, "rgba(0,0,0,0.6)");
    ao.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = ao;
    ctx.fillRect(g.cx - Rb, y0, Rb * 2, g.bodyH);
    ctx.restore();

    // rim lip (front face)
    ctx.beginPath();
    ctx.ellipse(g.cx, g.cy, R, R * k, 0, 0, Math.PI);
    ctx.ellipse(g.cx, g.cy + g.lip, R, R * k, 0, Math.PI, 0, true);
    ctx.closePath();
    const lg = ctx.createLinearGradient(g.cx - R, 0, g.cx + R, 0);
    lg.addColorStop(0, "#10141f");
    lg.addColorStop(0.45, "#3a4459");
    lg.addColorStop(1, "#0d111b");
    ctx.fillStyle = lg;
    ctx.fill();
    // rim top ring
    ctx.beginPath();
    ctx.ellipse(g.cx, g.cy, R, R * k, 0, 0, TAU);
    ctx.ellipse(g.cx, g.cy, r, r * k, 0, TAU, 0, true);
    const tg = ctx.createLinearGradient(0, g.cy - R * k, 0, g.cy + R * k);
    tg.addColorStop(0, "#2a3246");
    tg.addColorStop(1, "#4a5670");
    ctx.fillStyle = tg;
    ctx.fill("evenodd");
    ctx.strokeStyle = "rgba(2,3,8,0.9)";
    ctx.lineWidth = 1.6;
    for (let i = 0; i < 18; i++) {
      const a = (i / 18) * TAU + 0.1;
      ctx.beginPath();
      ctx.moveTo(g.cx + Math.cos(a) * r, g.cy + Math.sin(a) * r * k);
      ctx.lineTo(g.cx + Math.cos(a) * R, g.cy + Math.sin(a) * R * k);
      ctx.stroke();
    }
    ctx.strokeStyle = "rgba(170,200,240,0.18)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.ellipse(g.cx, g.cy, R, R * k, 0, Math.PI * 1.05, Math.PI * 1.95);
    ctx.stroke();

    // shaft + water
    ctx.save();
    ctx.beginPath();
    ctx.ellipse(g.cx, g.cy, r - 1, r * k - 1, 0, 0, TAU);
    ctx.clip();
    const wall = ctx.createLinearGradient(0, g.cy - r * k, 0, g.cy + r * k);
    wall.addColorStop(0, "#1a2133");
    wall.addColorStop(1, "#03050a");
    ctx.fillStyle = wall;
    ctx.fillRect(g.cx - r, g.cy - r * k, r * 2, r * k * 2);
    const W = this.#waterEllipse();
    // bounce light on the wall
    ctx.globalCompositeOperation = "lighter";
    for (let i = 1; i <= 5; i++) {
      ctx.strokeStyle = `rgba(0,229,255,${0.06 * (1 - i / 6)})`;
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.ellipse(W.cx, W.cy - i * 3.5, W.rx, W.ry, 0, Math.PI, TAU);
      ctx.stroke();
    }
    ctx.globalCompositeOperation = "source-over";
    ctx.beginPath();
    ctx.ellipse(W.cx, W.cy, W.rx, W.ry, 0, 0, TAU);
    ctx.clip();
    const deep = ctx.createRadialGradient(W.cx, W.cy - W.ry * 0.3, W.rx * 0.05, W.cx, W.cy, W.rx);
    deep.addColorStop(0, "#19c6de");
    deep.addColorStop(0.4, "#067994");
    deep.addColorStop(0.8, "#033348");
    deep.addColorStop(1, "#021520");
    ctx.fillStyle = deep;
    ctx.fillRect(W.cx - W.rx, W.cy - W.ry, W.rx * 2, W.ry * 2);
    ctx.globalCompositeOperation = "lighter";
    const t = this.time;
    ctx.lineWidth = Math.max(1, W.rx * 0.014);
    for (let i = 0; i < 7; i++) {
      const base = -1 + (i + 0.5) * (2 / 7);
      ctx.strokeStyle = `rgba(140,250,255,${0.07 + 0.04 * Math.sin(t * 1.4 + i)})`;
      ctx.beginPath();
      for (let s = 0; s <= 20; s++) {
        const q = -1 + s / 10;
        const v = base + 0.1 * Math.sin(q * 5 + t * 1.7 + i * 1.3) + 0.05 * Math.sin(q * 11 - t * 2.2);
        const x = W.cx + q * W.rx;
        const y = W.cy + v * W.ry;
        s ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
      }
      ctx.stroke();
    }
    // moving sheen
    const sx = W.cx + Math.sin(t * 0.5) * W.rx * 0.4;
    const sheen = ctx.createRadialGradient(sx, W.cy - W.ry * 0.35, 0, sx, W.cy - W.ry * 0.35, W.rx * 0.5);
    sheen.addColorStop(0, "rgba(200,255,255,0.18)");
    sheen.addColorStop(1, "rgba(200,255,255,0)");
    ctx.fillStyle = sheen;
    ctx.fillRect(W.cx - W.rx, W.cy - W.ry, W.rx * 2, W.ry * 2);
    for (const rp of this.ripples) {
      const p = rp.t / 2;
      const [cr, cg, cb] = heatRgb(rp.heat * 0.7);
      for (let ring = 0; ring < 3; ring++) {
        const rad = (rp.t * (0.3 + rp.s * 0.06) - ring * 0.07) * W.rx;
        if (rad <= 0) continue;
        const a = (1 - p) * (0.6 - ring * 0.17);
        if (a <= 0) continue;
        ctx.strokeStyle = `rgba(${cr},${cg},${cb},${a})`;
        ctx.lineWidth = Math.max(0.8, 2 * (1 - p));
        ctx.beginPath();
        ctx.ellipse(W.cx + rp.u * W.rx, W.cy + rp.v * W.ry, rad, rad * (W.ry / W.rx), 0, 0, TAU);
        ctx.stroke();
      }
    }
    ctx.globalCompositeOperation = "source-over";
    const edge = ctx.createRadialGradient(W.cx, W.cy, W.rx * 0.6, W.cx, W.cy, W.rx);
    edge.addColorStop(0, "rgba(0,0,0,0)");
    edge.addColorStop(1, "rgba(0,4,10,0.5)");
    ctx.fillStyle = edge;
    ctx.fillRect(W.cx - W.rx, W.cy - W.ry, W.rx * 2, W.ry * 2);
    ctx.restore();

    // waterline + inner lip glow
    ctx.save();
    ctx.shadowColor = "#00e5ff";
    ctx.shadowBlur = 12;
    ctx.strokeStyle = "rgba(90,245,255,0.7)";
    ctx.lineWidth = 1.3;
    ctx.beginPath();
    ctx.ellipse(W.cx, W.cy, W.rx, W.ry, 0, Math.PI * 1.03, Math.PI * 1.97);
    ctx.stroke();
    ctx.strokeStyle = `rgba(0,229,255,${0.4 + 0.15 * Math.sin(this.time * 1.6)})`;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.ellipse(g.cx, g.cy, r, r * k, 0, 0, TAU);
    ctx.stroke();
    ctx.restore();

    // splash + mist
    ctx.save();
    ctx.globalCompositeOperation = "lighter";
    for (const m of this.mist) {
      const a = Math.sin((m.t / m.life) * Math.PI) * 0.16;
      ctx.globalAlpha = a;
      ctx.drawImage(this.cyan, m.x - m.r, m.y - m.r, m.r * 2, m.r * 2);
    }
    for (const p of this.splash) {
      const a = 1 - p.t / p.life;
      ctx.globalAlpha = a * 0.9;
      const s = p.r * (0.7 + 0.5 * a) * 2;
      ctx.drawImage(p.heat > 0.5 ? this.amber : this.cyan, p.x - s / 2, p.y - s / 2, s, s);
    }
    ctx.restore();
  }

  #drawBuckets() {
    const { ctx, g } = this;
    const fs = g.narrow ? 10.5 : 12;
    let maxRecv = 0;
    for (const h of this.holders) maxRecv = Math.max(maxRecv, h.shown / h.share);
    for (const h of this.holders) {
      const { x, bottom, bw, bh } = h.geo;
      const top = bottom - bh;
      const tw = bw * 0.5; // half width at the mouth
      const bwb = bw * 0.36; // half width at the base
      const ek = 0.32;
      // shadow
      ctx.fillStyle = "rgba(0,0,0,0.45)";
      ctx.beginPath();
      ctx.ellipse(x, bottom + 2, tw * 1.25, tw * ek * 1.2, 0, 0, TAU);
      ctx.fill();
      // vessel body path
      const body = () => {
        ctx.beginPath();
        ctx.moveTo(x - tw, top);
        ctx.lineTo(x - bwb, bottom);
        ctx.ellipse(x, bottom, bwb, bwb * ek, 0, Math.PI, 0, true);
        ctx.lineTo(x + tw, top);
        ctx.ellipse(x, top, tw, tw * ek, 0, 0, Math.PI, false);
        ctx.closePath();
      };
      ctx.save();
      body();
      const glass = ctx.createLinearGradient(x - tw, 0, x + tw, 0);
      glass.addColorStop(0, "rgba(20,30,50,0.85)");
      glass.addColorStop(0.5, "rgba(36,48,72,0.7)");
      glass.addColorStop(1, "rgba(12,18,32,0.9)");
      ctx.fillStyle = glass;
      ctx.fill();
      ctx.clip();
      // water fill: every holder's bucket fills at the same relative pace because it is sized by its share
      const fill = clamp(maxRecv > 0 ? 0.08 + 0.84 * (1 - Math.exp(-(h.shown / h.share) / 1.4)) : 0, 0, 0.92);
      if (fill > 0.001) {
        const wy = bottom - fill * bh;
        const wg = ctx.createLinearGradient(0, wy, 0, bottom);
        wg.addColorStop(0, "rgba(95,243,255,0.95)");
        wg.addColorStop(1, "rgba(0,90,120,0.95)");
        ctx.fillStyle = wg;
        ctx.fillRect(x - tw - 2, wy, tw * 2 + 4, bottom - wy + 10);
        const wHalf = lerp(bwb, tw, fill);
        ctx.fillStyle = `rgba(200,255,255,${0.55 + h.pulse * 0.4})`;
        ctx.beginPath();
        ctx.ellipse(x, wy, wHalf, wHalf * ek * 0.8, 0, 0, TAU);
        ctx.fill();
      }
      ctx.restore();
      // outline with cyan edge glow
      ctx.save();
      body();
      ctx.shadowColor = "#00e5ff";
      ctx.shadowBlur = 8 + h.pulse * 14;
      ctx.strokeStyle = `rgba(0,229,255,${0.45 + h.pulse * 0.4})`;
      ctx.lineWidth = 1.3;
      ctx.stroke();
      ctx.restore();
      ctx.strokeStyle = "rgba(190,240,255,0.5)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.ellipse(x, top, tw, tw * ek, 0, Math.PI, TAU);
      ctx.stroke();
      // labels
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      ctx.fillStyle = "#e6f1ff";
      ctx.font = `600 ${fs}px Manrope, system-ui, sans-serif`;
      ctx.fillText(`Holder ${h.id}`, x, bottom + 10);
      ctx.fillStyle = "#8a9ab4";
      ctx.font = `500 ${fs - 1}px Manrope, system-ui, sans-serif`;
      ctx.fillText(`${Math.round(h.share * 100)}% of stake`, x, bottom + 12 + fs);
      ctx.fillStyle = "#5ff3ff";
      ctx.font = `600 ${fs - 0.5}px "JetBrains Mono", ui-monospace, monospace`;
      ctx.fillText(`+${h.shown.toFixed(3)}`, x, bottom + 15 + fs * 2);
    }
  }

  #bez(P, t) {
    const u = 1 - t;
    return { x: u * u * P.sx + 2 * u * t * P.cx + t * t * P.ex, y: u * u * P.sy + 2 * u * t * P.cy + t * t * P.ey };
  }

  #drawStreams() {
    const { ctx } = this;
    ctx.save();
    ctx.lineCap = "round";
    const paths = new Map();
    for (const h of this.holders) {
      const P = this.#path(h);
      paths.set(h, P);
      const f = clamp((h.flow / h.share) * 7, 0, 1);
      // faint guide channel (always visible, so the routes are readable even when the well is dry)
      ctx.setLineDash([2, 7]);
      ctx.lineDashOffset = 0;
      ctx.strokeStyle = "rgba(0,229,255,0.16)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(P.sx, P.sy);
      ctx.quadraticCurveTo(P.cx, P.cy, P.ex, P.ey);
      ctx.stroke();
      if (f > 0.01) {
        ctx.setLineDash([10, 12]);
        ctx.lineDashOffset = this.reduced ? 0 : -this.time * 90;
        ctx.strokeStyle = `rgba(95,243,255,${0.12 + f * 0.45})`;
        ctx.shadowColor = "#00e5ff";
        ctx.shadowBlur = 10;
        ctx.lineWidth = 1 + h.share * 9 * f;
        ctx.beginPath();
        ctx.moveTo(P.sx, P.sy);
        ctx.quadraticCurveTo(P.cx, P.cy, P.ex, P.ey);
        ctx.stroke();
        ctx.shadowBlur = 0;
      }
    }
    ctx.setLineDash([]);
    ctx.globalCompositeOperation = "lighter";
    for (const s of this.streams) {
      if (s.p < 0) continue;
      const P = paths.get(s.h);
      const q = this.#bez(P, s.p);
      const size = 7 + s.h.share * 16;
      ctx.globalAlpha = 0.95 * Math.min(1, 0.4 + s.p * 2);
      ctx.drawImage(this.cyan, q.x - size / 2 + s.j, q.y - size / 2, size, size);
    }
    ctx.restore();
  }

  #drawDrops() {
    const { ctx } = this;
    ctx.save();
    for (const d of this.drops) {
      const [r, g, b] = heatRgb(d.heat);
      const s = d.s;
      const tr = ctx.createLinearGradient(d.x, d.y - s * 7, d.x, d.y);
      tr.addColorStop(0, `rgba(${r},${g},${b},0)`);
      tr.addColorStop(1, `rgba(${r},${g},${b},0.5)`);
      ctx.strokeStyle = tr;
      ctx.lineWidth = s * 0.55;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(d.x, d.y - s * 7);
      ctx.lineTo(d.x, d.y - s);
      ctx.stroke();
      ctx.shadowColor = `rgb(${r},${g},${b})`;
      ctx.shadowBlur = 16;
      const body = ctx.createRadialGradient(d.x - s * 0.3, d.y - s * 0.3, 0, d.x, d.y, s * 1.2);
      body.addColorStop(0, "#ffffff");
      body.addColorStop(0.45, `rgb(${r},${g},${b})`);
      body.addColorStop(1, `rgba(${r >> 1},${g >> 1},${b >> 1},1)`);
      ctx.fillStyle = body;
      ctx.beginPath();
      ctx.moveTo(d.x, d.y - s * 2.2);
      ctx.bezierCurveTo(d.x + s * 0.4, d.y - s * 1.1, d.x + s, d.y - s * 0.4, d.x + s, d.y + s * 0.15);
      ctx.arc(d.x, d.y + s * 0.15, s, 0, Math.PI, false);
      ctx.bezierCurveTo(d.x - s, d.y - s * 0.4, d.x - s * 0.4, d.y - s * 1.1, d.x, d.y - s * 2.2);
      ctx.fill();
      ctx.shadowBlur = 0;
    }
    ctx.restore();
  }

  #drawPopups() {
    const { ctx, g } = this;
    ctx.save();
    ctx.textAlign = "center";
    ctx.font = `600 ${g.narrow ? 11 : 12.5}px "JetBrains Mono", ui-monospace, monospace`;
    for (const p of this.popups) {
      const a = 1 - p.t / 1.4;
      const [r, gg, b] = heatRgb(p.heat);
      ctx.fillStyle = `rgba(${lerp(r, 220, 0.4) | 0},${lerp(gg, 255, 0.4) | 0},${lerp(b, 255, 0.4) | 0},${a})`;
      ctx.fillText(p.text, p.x, p.y - g.r * g.k * 0.6 - p.t * 34);
    }
    ctx.restore();
  }

  #drawWatermark() {
    const { ctx, w, h, g } = this;
    ctx.save();
    ctx.textAlign = "left";
    ctx.textBaseline = "bottom";
    ctx.font = `600 ${g.narrow ? 9.5 : 10.5}px "JetBrains Mono", ui-monospace, monospace`;
    ctx.fillStyle = "rgba(255,196,87,0.55)";
    const txt = "SIMULATION · NOT REAL TRADES";
    ctx.fillText(txt.split("").join(String.fromCharCode(8202)), 14, h - 10);
    void w;
    ctx.restore();
  }
}
